import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import unset from 'lodash/unset';
import get from 'lodash/get';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { Typography } from '@mui/material';

import {
  tableOptions as defaultTableOptions,
  VEHICLES_TABLE_TITLE,
  VEHICLES_CSV_COLUMN_NAME,
} from '../../lib/table-utils';

const ASCENDING = 'asc';
const DESCENDING = 'desc';

const TABLE_SEARCH = 'search';

const DISPLAY_DATA_LENGTH = 'displayDataLength';
const TABLE_ROWS_SELECTED = 'rowsSelected';
const TABLE_SEARCH_TEXT = 'searchText';
const COLUMNS = 'columns';
const SKIP_SAVING_FIELDS = [DISPLAY_DATA_LENGTH, TABLE_ROWS_SELECTED, TABLE_SEARCH_TEXT, COLUMNS];

const withPersistentSettings = (WrappedComponent, saveStateKey) => class extends Component {
  constructor(props) {
    super(props);
    const { defaultFilter, defaultSortName, defaultSortDirection } = props;
    this.state = {
      tableTitle: '',
      searchText: '',
      selectedFilters: defaultFilter || [],
      rowsPerPage: 20,
      primarySort: {
        name: defaultSortName || '',
        direction: defaultSortDirection || '',
      },
      [DISPLAY_DATA_LENGTH]: 0,
      rowsSelected: [],
      displayColumns: [],
      columns: [],
    };

    this.getTitleFromData = this.getTitleFromData.bind(this);
    this.clearSelectedRows = this.clearSelectedRows.bind(this);
    this.addToColumnData = this.addToColumnData.bind(this);
  }

  componentDidMount() {
    const prevState = JSON.parse(localStorage.getItem(saveStateKey));
    if (prevState) {
      SKIP_SAVING_FIELDS.forEach((field) => unset(prevState, field));
      this.setState(prevState);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (saveStateKey) {
      const stateToSave = { ...this.state };
      const previousSavedState = { ...prevState };
      SKIP_SAVING_FIELDS.forEach((field) => {
        unset(stateToSave, field);
        unset(previousSavedState, field);
      });
      if (!isEqual(stateToSave, previousSavedState)) { // does a deep-compare for Arrays, etc
        localStorage.setItem(saveStateKey, JSON.stringify(stateToSave));
      }
    }
  }

  get tableOptions() {
    const { tableOptions: priorTableOptions } = this.props;
    const {
      rowsPerPage, searchText, rowsSelected, displayDataLength, displayColumns, columns,
    } = this.state;
    const basicOptions = { ...defaultTableOptions, ...priorTableOptions };

    basicOptions.rowsPerPage = rowsPerPage;

    basicOptions.textLabels = {
      selectedRows: {
        text: `row(s) selected of ${displayDataLength}`,
      },
    };

    basicOptions.customToolbarSelect = () => {};

    basicOptions.onChangeRowsPerPage = (newRowsPerPage) => {
      this.setState({ rowsPerPage: newRowsPerPage });
    };

    const previousOnFilterChange = basicOptions.onFilterChange;
    basicOptions.onFilterChange = (changedColumn, filterList) => {
      if (previousOnFilterChange) previousOnFilterChange(changedColumn, filterList);
      this.setState({ selectedFilters: filterList });
    };

    const previousOnInit = basicOptions.onTableInit;
    basicOptions.onTableInit = (action, tableState) => {
      const stateUpdate = {};

      const updatedDisplayDataCount = get(tableState, 'displayData.length', displayDataLength);
      if (displayDataLength !== updatedDisplayDataCount) {
        stateUpdate.displayDataLength = updatedDisplayDataCount;
      }

      if (!isEmpty(stateUpdate)) {
        this.setState(() => stateUpdate);
      }
      if (previousOnInit) previousOnInit(action, tableState);
    };

    basicOptions.onTableChange = (action, tableMeta) => {
      const updatedDisplayDataCount = get(tableMeta, 'displayData.length', displayDataLength);
      const stateUpdate = {};

      if (displayDataLength !== updatedDisplayDataCount) {
        stateUpdate.displayDataLength = updatedDisplayDataCount;
      }

      if (action === TABLE_SEARCH && tableMeta.searchText !== searchText) {
        stateUpdate.searchText = tableMeta.searchText;
      }

      if (!isEmpty(stateUpdate)) {
        this.setState(() => stateUpdate);
      }
    };

    basicOptions.onDownload = (buildHead, buildBody, cols, data) => {
      const { selectedFilters, tableTitle } = this.state;
      const tableColumns = cols;
      if (tableTitle === VEHICLES_TABLE_TITLE) {
        tableColumns[1].name = VEHICLES_CSV_COLUMN_NAME;
        tableColumns[1].label = VEHICLES_CSV_COLUMN_NAME;
      }
      const head = buildHead(tableColumns);

      const filteredData = data.filter(({ data: dataArray }) => {
        const filterResults = selectedFilters.map((filterColumnValues, idx) => {
          if (isEmpty(filterColumnValues)) return true;

          const dataValue = get(dataArray, idx);
          return filterColumnValues.includes(dataValue);
        });
        return every(filterResults);
      });
      const body = buildBody(filteredData);

      return head + body;
    };

    basicOptions.onColumnSortChange = (changedColumn, direction) => {
      let order = DESCENDING;
      if (direction === 'ascending') {
        order = ASCENDING;
      }
      this.setState({
        primarySort: {
          name: changedColumn,
          direction: order,
        },
      });
    };

    basicOptions.searchText = searchText;

    basicOptions.rowsSelected = rowsSelected;
    basicOptions.onRowSelectionChange = (currentSelection, allSelected) => {
      const currentRowsSelected = allSelected.map((r) => r.dataIndex);
      this.setState({ rowsSelected: currentRowsSelected });
    };

    basicOptions.onColumnViewChange = (changedColumn, action) => {
      const currentDisplayColumns = [...displayColumns];
      for (let i = 0; i < columns.length; i++) {
        const thisCol = columns[i];
        if (thisCol.name === changedColumn) {
          if (action === 'add') {
            currentDisplayColumns[i] = 'true';
          } else {
            currentDisplayColumns[i] = 'false';
          }
          break;
        }
      }
      this.setState({ displayColumns: currentDisplayColumns });
    };

    return basicOptions;
  }

  getTitleFromData(baseTitle, tableData, timestampString = '') {
    const { displayDataLength, tableTitle } = this.state;
    let displayCount = displayDataLength;
    if (isNil(displayCount)) displayCount = get(tableData, 'length', 0); // fall back to table data value
    if (baseTitle && baseTitle !== tableTitle) this.setState({ tableTitle: baseTitle });
    const timeStampDisplay = (
      <Typography variant="caption">
        {timestampString}
      </Typography>
    );
    return (
      <>
        {baseTitle && baseTitle !== VEHICLES_TABLE_TITLE
          ? (
            <Typography variant="h6">
              {baseTitle}
              &nbsp;(
              {displayCount}
              &nbsp;Results)
              {timeStampDisplay}
            </Typography>
          ) : ('')}
      </>
    );
  }

  addToColumnData(columnData, selectedFilters, primarySort) {
    const { displayColumns, columns } = this.state;
    const updateStorage = displayColumns.length === 0
    || displayColumns.length !== columnData.length;
    let currentDisplayColumns = [...displayColumns];
    if (updateStorage) {
      currentDisplayColumns = [];
    }

    for (let i = 0; i < columnData.length; i++) {
      const thisCol = columnData[i];
      set(thisCol, 'options.filterList', selectedFilters[i]);

      if (primarySort.name === thisCol.name) {
        set(thisCol, 'options.sortOrder', primarySort.direction);
      }

      if (updateStorage) {
        currentDisplayColumns.push(columnData[i].options ? columnData[i].options.display || 'true' : 'true');
      } else {
        set(thisCol, 'options.display', displayColumns[i]);
      }
    }

    if (updateStorage) {
      this.setState({ displayColumns: currentDisplayColumns });
    }
    if (columns.length === 0) {
      this.setState({ columns: columnData });
    }

    return columnData;
  }

  clearSelectedRows() {
    // generally for use in components where we allow bulk selecting.
    // this function would be called in the .then after the bulk action
    // is fired, to clear selected rows.
    return this.setState({ rowsSelected: [] });
  }

  render() {
    const {
      selectedFilters,
      rowsPerPage,
      primarySort,
    } = this.state;

    return (
      <WrappedComponent
        {...this.props}
        selectedFilters={selectedFilters}
        rowsPerPage={rowsPerPage}
        primarySort={primarySort}
        tableOptions={this.tableOptions}
        addToColumnData={this.addToColumnData}
        getTitleFromData={this.getTitleFromData}
        clearSelectedRows={this.clearSelectedRows}
      />
    );
  }
};

export default withPersistentSettings;
